import { useEffect, useRef } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { UI_ORDER_TYPES, UIOrderType } from '@drift/common';
import { useSyncLocalStorage } from 'src/hooks/useSyncLocalStorage';

const getTradeFormOrderType = (orderType: UIOrderType) => {
	const currentOrderTypeIsValid = !!UI_ORDER_TYPES[orderType];

	return currentOrderTypeIsValid ? orderType : 'market';
};

/**
 * Hook that manages persistence and hydration of the last used order type for the trade form.
 *
 * This hook performs two main functions:
 * 1. Hydrates the trade form with the last used order type from localStorage on mount
 * 2. Persists the current order type to localStorage whenever it changes
 *
 * @param currentOrderType - The current order type from the trade form state
 *
 * Behavior:
 * - Allows all valid order types from UI_ORDER_TYPES, defaults to 'market'
 * - Automatically syncs between trade form state and localStorage
 */
export const usePersistAndHydrateLastUsedOrderType = (
	currentOrderType: UIOrderType
) => {
	const setState = useDriftStore((s) => s.set);
	const [lastUsedOrderType, setLastUsedOrderType] =
		useSyncLocalStorage<UIOrderType>('lastUsedPerpOrderType', 'market');

	const hasHydratedOnce = useRef(false);

	// Hydrate once from localStorage
	useEffect(() => {
		if (hasHydratedOnce.current) return;

		const tradeFormOrderType = getTradeFormOrderType(lastUsedOrderType);

		setState((s) => {
			s.tradeForm.orderType = tradeFormOrderType;
		});

		hasHydratedOnce.current = true;
	}, [setState, lastUsedOrderType]);

	// Persist to localStorage when form state changes
	useEffect(() => {
		if (lastUsedOrderType !== currentOrderType) {
			// for some reason, this condition is needed to prevent infinite loop of the hook
			setLastUsedOrderType(currentOrderType);
		}
	}, [currentOrderType, setLastUsedOrderType]);
};
